import { useState, useRef, useEffect } from 'react';
import { useVideoAnimate } from '@genfun/core/hooks/useVideoAnimate';
import { fetchGallery, fetchRemixData } from '@genfun/core/api/API';
import { LoginPopup } from '../popups/LoginPopup';
import { SubscriptionPopup } from '../popups/SubscriptionPopup';
import { AccountPopup } from '../popups/AccountPopup';
import { PhotoCreatePopup } from '../popups/PhotoCreatePopup';
import { getSamplesForAction } from '../data/videoSamples';
import { SiteFooter } from '../components/SiteFooter';
// TapGame temporarily disabled
// import { TapGame } from '../components/TapGame';
import { downloadFile } from '../utils/download';
import './Video.css';
import '../popups/PhotoCreatePopup.css';

function useReveal() {
    const ioRef = useRef(null);
    useEffect(() => {
        if (!ioRef.current) {
            ioRef.current = new IntersectionObserver((entries) => {
                entries.forEach(e => {
                    if (e.isIntersecting) {
                        e.target.classList.add('revealed');
                        ioRef.current.unobserve(e.target);
                    }
                });
            }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
        }
        document.querySelectorAll('.vidPage [data-reveal]:not(.revealed)')
            .forEach(el => ioRef.current.observe(el));
    });
    useEffect(() => () => { ioRef.current?.disconnect(); ioRef.current = null; }, []);
}

function HeaderMenu({ user, onNavigate, onSignOut }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDocDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocDown);
        document.addEventListener('touchstart', onDocDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocDown);
            document.removeEventListener('touchstart', onDocDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="vidHeaderMenu" ref={wrapRef}>
            <button
                className={`vidHeaderMenuBtn${open ? ' vidHeaderMenuBtn--open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-label="More menu"
                aria-expanded={open}
            >
                <span aria-hidden="true">⋯</span>
            </button>
            {open && (
                <div className="vidHeaderMenuDropdown" role="menu">
                    <button className="vidHeaderMenuItem vidHeaderMenuItem--admin" role="menuitem" onClick={() => { setOpen(false); }}>Generate video</button>
                    <button className="vidHeaderMenuItem" role="menuitem" onClick={() => { setOpen(false); onNavigate('photo'); }}>Generate photo</button>
                    <button className="vidHeaderMenuItem" role="menuitem" onClick={() => { setOpen(false); onNavigate('gallery'); }}>Gallery</button>
                    {user && (
                        <button
                            className="vidHeaderMenuItem vidHeaderMenuItem--danger"
                            role="menuitem"
                            onClick={() => { setOpen(false); onSignOut(); }}
                        >
                            Sign out
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// D33P neo-brutalist design has no ambient collage/blob/grain backdrop.
function AmbientBackdrop() {
    return null;
}

const CDN = 'https://genres.nyc3.cdn.digitaloceanspaces.com/claude';
const P0RN_CDN = 'https://genres.nyc3.cdn.digitaloceanspaces.com/p0rn';
const P0RN_LEGACY_CDN = 'https://genres.nyc3.cdn.digitaloceanspaces.com/thumbnails/video_gen/videos';

const VIDEO_ACTIONS = [
    { key: 'cumshot', title: 'Cumshot', emoji: '💦', video: `${P0RN_CDN}/cumshot_small.mp4`, photo: `${CDN}/photo/cumshot_photo.png`, thumbnail: `${CDN}/thumbnail/cumshot_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/cumshot.jpg`, featured: true, badge: 'POPULAR' },
    { key: 'reverse_cowgirl', title: 'Cowgirl', emoji: '🤠', video: `${P0RN_CDN}/cowgirl_small.mp4`, photo: `${CDN}/photo/cow_girl_photo.png`, thumbnail: `${CDN}/thumbnail/cow_girl_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/cow_girl.jpg`, badge: 'HOT' },
    { key: 'blowjob', title: 'Blowjob', emoji: '👄', video: `${P0RN_CDN}/blowjob_small.mp4`, photo: `${CDN}/photo/blow_job_photo.png`, thumbnail: `${CDN}/thumbnail/blow_job_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/blow_job.jpg`, badge: 'HOT' },
    { key: 'fingering', title: 'Fingering', emoji: '👆', video: `${P0RN_CDN}/fingering_small.mp4`, photo: `${CDN}/photo/fingering_photo.png`, thumbnail: `${CDN}/thumbnail/fingering_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/fingering.jpg` },
    { key: 'twerking', title: 'Twerking', emoji: '🍑', video: `${P0RN_CDN}/twerking_small.mp4`, photo: `${CDN}/photo/twerking_photo.png`, thumbnail: `${CDN}/thumbnail/twerking_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/twerking.jpg`, badge: 'HOT' },
    { key: 'full_nelson', title: 'Full Nelson', emoji: '🤼', video: `${P0RN_LEGACY_CDN}/full_nelson_small.mp4`, photo: `${CDN}/photo/full_nelson_photo.png`, thumbnail: `${CDN}/thumbnail/full_nelson_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/full_nelson.jpg` },
    { key: 'posing', title: 'Posing', emoji: '💃', video: `${P0RN_CDN}/posing_small.mp4`, photo: `${CDN}/photo/posing_photo.png`, thumbnail: `${CDN}/thumbnail/posing_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/posing.jpg` },
    { key: 'pov_insertion', title: 'POV Insertion', emoji: '🍆', video: `${P0RN_CDN}/pov_insertion_small.mp4`, photo: `${CDN}/photo/pov_insertion_photo.png`, thumbnail: `${CDN}/thumbnail/pov_insertion_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/pov_insertion.jpg`, badge: 'NEW' },
    { key: 'rimming', title: 'Rimming', emoji: '👅', video: `${P0RN_CDN}/rimming_small.mp4`, photo: `${CDN}/photo/rimming_photo.png`, thumbnail: `${CDN}/thumbnail/rimming_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/rimming.jpg` },
    { key: 'titfuck', title: 'Titfuck', emoji: '🍈', video: `${P0RN_CDN}/titfuck_small.mp4`, photo: `${CDN}/photo/titfuck_photo.png`, thumbnail: `${CDN}/thumbnail/titfuck_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/titfuck.jpg`, badge: 'NEW' },
    { key: 'fisting', title: 'Fisting', emoji: '✊', video: `${P0RN_CDN}/fisting_small.mp4`, photo: `${CDN}/thumbnail/fisting_thumbnail.jpg`, thumbnail: `${CDN}/thumbnail/fisting_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/fisting.jpg` },
];

const VID_MESSAGES = ['Creating magic', 'Rendering frames', 'Polishing details', 'Almost there'];

function ProgressBar({ durationMs, startTime }) {
    const [progress, setProgress] = useState(0);
    const [msgIdx, setMsgIdx] = useState(0);
    const startRef = useRef(startTime || Date.now());
    useEffect(() => {
        const interval = setInterval(() => {
            const elapsed = Date.now() - startRef.current;
            setProgress(Math.min(95, (elapsed / durationMs) * 100));
        }, 300);
        return () => clearInterval(interval);
    }, [durationMs]);
    useEffect(() => {
        const interval = setInterval(() => {
            setMsgIdx(i => (i + 1) % VID_MESSAGES.length);
        }, 3500);
        return () => clearInterval(interval);
    }, []);
    const secsLeft = Math.max(0, Math.round((durationMs - (Date.now() - startRef.current)) / 1000));
    return (
        <div className="vidProgressWrap">
            <div className="vidProgressBar" style={{ width: `${progress}%` }} />
            <div className="vidProgressText">{VID_MESSAGES[msgIdx]}...</div>
            <div className="vidProgressTime">~{secsLeft}s remaining</div>
        </div>
    );
}

function LoadingSection({ durationMs, action }) {
    const [startTime] = useState(() => Date.now());
    const progressRef = useRef(null);
    useEffect(() => {
        const t = setTimeout(() => {
            progressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 400);
        return () => clearTimeout(t);
    }, []);
    return (
        <div className="vidLoadingFrame">
            <div className="vidLoadingFrameCollage" aria-hidden="true">
                {VIDEO_ACTIONS.slice(0, 4).map((a, i) => (
                    <img key={i} className={`vidLoadingFrameCollageImg vidLoadingFrameCollageImg--${i}`} src={a.firstFrame} alt="" />
                ))}
                <div className="vidLoadingFrameCollageBlob" />
            </div>
            <div className="vidLoadingFrameInner">
                {action && (
                    <div className="vidLoadingNowPill">
                        <span className="vidLoadingNowDot" />
                        Now rendering: <strong>{action.title}</strong>
                    </div>
                )}
                <div className="loadingSection">
                    <div className="loadingBigTitle">
                        <div className="loadingBigTitleMain">Rendering</div>
                        <div className="loadingBigTitleSub">Hang tight</div>
                    </div>
                    <div ref={progressRef} style={{ width: '100%' }}>
                        <ProgressBar durationMs={durationMs} startTime={startTime} />
                    </div>
                </div>
                <div className="vidLoadingTip">
                    <span aria-hidden="true">💡</span>
                    Don't close this tab — your video is rendering
                </div>
            </div>
        </div>
    );
}

function useLockBodyScroll(isOpen) {
    useEffect(() => {
        if (!isOpen) return;
        const html = document.documentElement;
        const body = document.body;
        const scrollbarWidth = window.innerWidth - html.clientWidth;
        const prev = {
            bodyOverflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
            bodyPaddingRight: body.style.paddingRight,
        };
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            body.style.paddingRight = `${scrollbarWidth}px`;
        }
        return () => {
            body.style.overflow = prev.bodyOverflow;
            html.style.overflow = prev.htmlOverflow;
            body.style.paddingRight = prev.bodyPaddingRight;
        };
    }, [isOpen]);
}

function ActionPickerPopup({ isOpen, onClose, selectedKey, onSelect }) {
    useLockBodyScroll(isOpen);
    if (!isOpen) return null;
    const allActions = VIDEO_ACTIONS;
    return (
        <div className="vidPopupOverlay" onClick={onClose}>
            <div className="vidPopup vidPopupWide" onClick={e => e.stopPropagation()}>
                <button className="vidPopupClose" onClick={onClose}>&times;</button>
                <div className="vidPopupHeader">
                    <h2 className="vidPopupTitle">Change action</h2>
                </div>
                <div className="vidActionPickerGrid">
                    {allActions.map(a => (
                        <button
                            key={a.key}
                            className={`vidActionPickerCard${a.key === selectedKey ? ' selected' : ''}`}
                            onClick={() => { onSelect(a); onClose(); }}
                        >
                            <span className="vidActionPickerEmoji" aria-hidden="true">{a.emoji}</span>
                            <span className="vidActionPickerTitle">{a.title}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StudioVideoCard({ action }) {
    return (
        <div className="vidStudioPaneInner vidStudioPaneInner--emoji">
            <span className="vidStudioEmoji" aria-hidden="true">{action.emoji}</span>
        </div>
    );
}

export function Video({ token, user, setSignedIn, onNavigate }) {
    const [selectedAction, setSelectedAction] = useState(VIDEO_ACTIONS[0]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImagePicker, setShowImagePicker] = useState(false);
    const [showActionPicker, setShowActionPicker] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [showPricing, setShowPricing] = useState(false);
    const [showAccount, setShowAccount] = useState(false);

    const [video, setVideo] = useState(null);
    const [genType, setGenType] = useState(null);
    const [generated, setGenerated] = useState(false);
    const [, setTaskId] = useState(null);
    const [photoGallery, setPhotoGallery] = useState([]);
    const [selectedImageFull, setSelectedImageFull] = useState(null);

    const studioRef = useRef(null);
    const resultRef = useRef(null);

    const { handleAnimate, error } = useVideoAnimate({
        token, user, selectedImage,
        action: selectedAction?.key || null,
        callbacks: {
            setGenType, setGenerated, setVideo, setTaskId,
            setShowLoginPopup: setShowLogin,
            onScrollToResults: () => { resultRef.current?.scrollIntoView({ behavior: 'smooth' }); },
        }
    });

    useEffect(() => {
        if (!selectedImage?.mediaId || !token) { setSelectedImageFull(null); return; }
        setSelectedImageFull(null);
        fetchRemixData(token, selectedImage.mediaId).then(data => {
            if (data?.url) setSelectedImageFull(data.url);
        }).catch(() => {});
    }, [selectedImage?.mediaId, token]);

    useEffect(() => {
        if (!token) return;
        fetchGallery([], 0, false, token, false, false, true, 200).then(data => {
            if (data?.content) setPhotoGallery(data.content);
        }).catch(() => {});
    }, [token]);

    const handleGenerate = () => {
        if (!user) { setShowLogin(true); return; }
        if (!user.credits) { setShowPricing(true); return; }
        if (!selectedImage) { setShowImagePicker(true); return; }
        handleAnimate();
    };

    const handleCreateNew = () => {
        setSelectedImage(null);
        setVideo(null);
        setGenerated(false);
        setGenType(null);
        studioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const sampleImages = selectedAction?.key ? getSamplesForAction(selectedAction.key) : [];

    const showLoading = !!genType && !generated;
    const showResult = !!video && generated;
    const [savedToast, setSavedToast] = useState(false);
    const handleSaveToast = () => {
        setSavedToast(true);
        setTimeout(() => setSavedToast(false), 1600);
    };

    useReveal();

    const signOut = () => {
        localStorage.removeItem('igenfuntoken');
        window.location.reload();
    };

    return (
        <div className="vidPage">
            <AmbientBackdrop />

            {/* Header */}
            <div className="vidHeader">
                <div className="vidHeaderLeft">
                    <span className="vidLogo" style={{ cursor: 'pointer' }} onClick={() => onNavigate('landing')}>D33P</span>
                    <nav className="vidNav">
                        <button className="vidNavBtn vidNavBtn--admin vidNavBtn--active">Generate video</button>
                        <button className="vidNavBtn" onClick={() => onNavigate('photo')}>Generate photo</button>
                        <button className="vidNavBtn" onClick={() => onNavigate('gallery')}>Gallery</button>
                    </nav>
                </div>
                <div className="vidHeaderRight">
                    {!user ? (
                        <button className="vidLoginBtn" onClick={() => setShowLogin(true)}>Log in</button>
                    ) : (
                        <>
                            <button className="vidCredits" onClick={() => setShowAccount(true)}>{user.credits ?? 0} tokens</button>
                            <button className="vidSignOutBtn vidSignOutBtn--desktopOnly" onClick={signOut}>Sign out</button>
                        </>
                    )}
                    <HeaderMenu user={user} onNavigate={onNavigate} onSignOut={signOut} />
                </div>
            </div>

            {/* Hero */}
            <section className="vidHero vidHero--compact" data-reveal>
                <h1 className="vidHeroTitle vidHeroTitle--compact">
                    <span className="vidHeroTitleAccent">Generate video</span>
                </h1>
            </section>

            {/* Studio: photo (left) → video (right) */}
            <div ref={studioRef} className="vidStudio" data-reveal>
                <div className="vidStudioGrid">
                    <button
                        className={`vidStudioPane vidStudioPane--photo${selectedImage ? ' picked' : ''}`}
                        onClick={() => setShowImagePicker(true)}
                    >
                        <span className="vidStudioPaneLabel">
                            <span className="vidStudioPaneLabelFull">[ STEP 1 ] PHOTO</span>
                            <span className="vidStudioPaneLabelShort">PHOTO</span>
                        </span>
                        {selectedImage ? (
                            <img className="vidStudioMedia" src={selectedImageFull || selectedImage.url} alt="" />
                        ) : (
                            <div className="vidStudioPanePlaceholder">
                                <span className="vidStudioPanePlus" aria-hidden="true">✨</span>
                                <span className="vidStudioPaneHint">Tap to generate</span>
                                <span className="vidStudioPaneNote">No uploads — synthetic only</span>
                            </div>
                        )}
                        <span className="vidStudioPaneCaption">{selectedImage ? 'Change' : 'Generate'}</span>
                    </button>

                    <div className="vidStudioArrow" aria-hidden="true">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                            <path d="M5 12h14m0 0l-5-5m5 5l-5 5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>

                    <button
                        className="vidStudioPane vidStudioPane--video picked"
                        onClick={() => setShowActionPicker(true)}
                    >
                        <span className="vidStudioPaneLabel">
                            <span className="vidStudioPaneLabelFull">[ STEP 2 ] ACTION</span>
                            <span className="vidStudioPaneLabelShort">ACTION</span>
                        </span>
                        <StudioVideoCard action={selectedAction} />
                        <span className="vidStudioPaneCaption">
                            <strong>{selectedAction.title}</strong>
                            <span className="vidStudioPaneCaptionExtra">
                                <span className="vidStudioPaneCaptionSep">·</span>
                                Tap to change
                            </span>
                        </span>
                    </button>
                </div>

                {!generated && (
                    <button
                        className="vidGenerateBtn"
                        onClick={handleGenerate}
                        disabled={showLoading}
                    >
                        {showLoading ? 'Generating...' : 'Generate video'}
                    </button>
                )}
                {generated && (
                    <button className="vidGenerateBtn" onClick={handleCreateNew}>
                        Create new
                    </button>
                )}

                {!generated && !showLoading && (
                    <div className={`vidGenerateHint${selectedImage ? '' : ' vidGenerateHint--missing'}`}>
                        {selectedImage ? (
                            <>
                                <span>1 TOKEN</span>
                                <span className="vidGenerateHintDot">/</span>
                                <span>~2 MIN RENDER</span>
                                <span className="vidGenerateHintDot">/</span>
                                <span>1080P</span>
                            </>
                        ) : (
                            <>
                                <span className="vidGenerateHintArrow">↑</span>
                                <span>Generate a photo to continue</span>
                            </>
                        )}
                    </div>
                )}

                {error && <div className="vidError">{error}</div>}
            </div>

            {/* Result */}
            <div ref={resultRef} />
            {(showLoading || showResult) && (
                <div className="vidResultSection">
                    {showLoading && <LoadingSection durationMs={120000} action={selectedAction} />}
                    {!showLoading && showResult && (
                        <div className="vidResultCard">
                            <div className="vidResultBurst" aria-hidden="true" />
                            <div className="vidResultMeta">
                                <span className="vidResultBadge">
                                    <span className="vidResultBadgeDot" /> Ready
                                </span>
                                <span className="vidResultMetaText">
                                    {selectedAction?.title || 'Video'} <span className="vidResultMetaDot">·</span> just now
                                </span>
                            </div>
                            <video className="vidResultVideo" src={video} autoPlay loop muted playsInline controls />
                            <div className="vidResultActions">
                                <button
                                    type="button"
                                    className="vidResultBtn vidResultBtn--primary"
                                    onClick={() => downloadFile(video)}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                    Download
                                </button>
                                <button type="button" className="vidResultBtn" onClick={handleSaveToast}>
                                    {savedToast ? (
                                        <>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                            Saved
                                        </>
                                    ) : (
                                        <>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>
                                            Save
                                        </>
                                    )}
                                </button>
                                <button type="button" className="vidResultBtn" onClick={handleCreateNew}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                                    Create another
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Action picker popup */}
            <ActionPickerPopup
                isOpen={showActionPicker}
                onClose={() => setShowActionPicker(false)}
                selectedKey={selectedAction?.key}
                onSelect={setSelectedAction}
            />

            {/* Photo creation popup */}
            <PhotoCreatePopup
                isOpen={showImagePicker}
                onClose={() => setShowImagePicker(false)}
                onNavigate={onNavigate}
                videoActionTitle={selectedAction?.title}
                photoGallery={photoGallery}
                sampleImages={sampleImages}
                onSelectExisting={(img) => {
                    setSelectedImage(img);
                    setShowImagePicker(false);
                }}
            />

            <LoginPopup isOpen={showLogin} setIsOpen={setShowLogin} setSignedIn={setSignedIn} />
            <SubscriptionPopup isOpen={showPricing} onClose={() => setShowPricing(false)} token={token} onNeedLogin={() => { setShowPricing(false); setShowLogin(true); }} />
            <AccountPopup isOpen={showAccount} onClose={() => setShowAccount(false)} token={token} user={user} />

            {/* Floating "now generating" pill */}
            {showLoading && selectedAction && (
                <button
                    className="vidNowGenPill"
                    onClick={() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                >
                    <span className="vidNowGenSpinner" aria-hidden="true">
                        <span className="vidNowGenSpinnerDot" />
                        <span className="vidNowGenSpinnerDot" />
                        <span className="vidNowGenSpinnerDot" />
                    </span>
                    <span>Generating <strong>{selectedAction.title}</strong></span>
                    <span className="vidNowGenPillArrow" aria-hidden="true">↑</span>
                </button>
            )}

            {/* Saved toast */}
            {savedToast && (
                <div className="vidSavedToast">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Saved to gallery
                </div>
            )}

            <SiteFooter onNavigate={onNavigate} />
        </div>
    );
}
